import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle, Shield, Eraser, Eye, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  contrat: 'Contrat',
  devis: 'Devis',
  bon_commande: 'Bon de commande',
  facture: 'Facture',
  bon_livraison: 'Bon de livraison',
  lettre_voiture: 'Lettre de voiture',
  bon_reception_chantier: 'Bon de réception chantier',
  pv_fin_chantier: "PV fin de chantier",
  contrat_eta: 'Contrat ETA',
  document_libre: 'Document',
};

const TYPE_ICONS = {
  contrat: '📄', devis: '💼', bon_commande: '📋', facture: '🧾',
  bon_livraison: '📦', lettre_voiture: '🚚',
  bon_reception_chantier: '🏗️', pv_fin_chantier: '✅',
  contrat_eta: '📄', document_libre: '✍️',
};

export default function SignerUniverselPage() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nomSignataire, setNomSignataire] = useState('');
  const [qualite, setQualite] = useState('');
  const [accepte, setAccepte] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [driveLink, setDriveLink] = useState(null);
  const [hasSignature, setHasSignature] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/signatures/public/sign/${token}`);
        if (!r.ok) throw new Error('Lien invalide');
        const d = await r.json();
        setDoc(d);
        if (d.statut === 'signe') setSigned(true);
        if (d.destinataire_nom) setNomSignataire(d.destinataire_nom);
      } catch (e) { toast.error("Lien invalide ou expiré"); }
      finally { setLoading(false); }
    })();
  }, [token]);

  // Attache des listeners NATIFS avec { passive: false } pour garantir que
  // preventDefault() marche sur les anciens mobiles (iOS Safari < 13, vieux Android).
  // React attache les listeners en mode passif par défaut, ce qui empêche preventDefault
  // de bloquer le scroll pendant qu'on signe → crash UX sur vieux tél.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || signed) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let drawing = false;

    const getPos = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
    };

    const start = (clientX, clientY) => {
      drawing = true;
      const { x, y } = getPos(clientX, clientY);
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const move = (clientX, clientY) => {
      if (!drawing) return;
      const { x, y } = getPos(clientX, clientY);
      ctx.lineTo(x, y);
      ctx.stroke();
      setHasSignature(true);
    };
    const end = () => { drawing = false; };

    // Souris (desktop)
    const onMouseDown = (e) => { e.preventDefault(); start(e.clientX, e.clientY); };
    const onMouseMove = (e) => { if (drawing) { e.preventDefault(); move(e.clientX, e.clientY); } };
    const onMouseUp = () => end();

    // Tactile (vieux mobiles) — passive:false pour que preventDefault fonctionne
    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length === 0) return;
      e.preventDefault();
      const t = e.touches[0];
      start(t.clientX, t.clientY);
    };
    const onTouchMove = (e) => {
      if (!drawing || !e.touches || e.touches.length === 0) return;
      e.preventDefault();
      const t = e.touches[0];
      move(t.clientX, t.clientY);
    };
    const onTouchEnd = (e) => { e.preventDefault(); end(); };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', onMouseUp);
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', onTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mouseleave', onMouseUp);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [loading, signed]);

  const clearSign = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!nomSignataire.trim()) { toast.error('Indique ton nom'); return; }
    if (!accepte) { toast.error('Accepte les conditions'); return; }
    if (!hasSignature) { toast.error('Signe dans le cadre'); return; }
    setSigning(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const r = await fetch(`${API}/api/signatures/public/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom_signataire: nomSignataire,
          qualite,
          image_base64: dataUrl,
          accept_conditions: true,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur signature');
      }
      const result = await r.json();
      setSigned(true);
      setDriveLink(result.drive_link);
      toast.success('Document signé et archivé !');
    } catch (e) { toast.error(e.message); }
    finally { setSigning(false); }
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Chargement...</div>;
  if (!doc) return <div className="p-10 text-center text-red-600">Document introuvable ou lien expiré.</div>;

  const typeLabel = TYPE_LABELS[doc.doc_type] || 'Document';
  const typeIcon = TYPE_ICONS[doc.doc_type] || '✍️';

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-10" data-testid="signer-universel-page">
          <div className="flex items-start gap-3 mb-6 pb-6 border-b-2 border-emerald-600">
            <div className="text-4xl">{typeIcon}</div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">{typeLabel}</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-emerald-800">{doc.titre}</h1>
              {doc.expediteur_nom && (
                <div className="mt-3 p-3 bg-amber-50 border-l-4 border-amber-500 rounded">
                  <p className="text-xs text-amber-700 uppercase font-semibold">📨 De la part de</p>
                  <p className="text-lg font-bold text-amber-900">{doc.expediteur_nom}</p>
                  {doc.expediteur_email && <p className="text-xs text-amber-700">{doc.expediteur_email}</p>}
                </div>
              )}
              {doc.marchandise && (
                <div className="mt-3 p-3 bg-emerald-50 rounded space-y-1 text-sm">
                  <p><strong>📦 Marchandise :</strong> {doc.marchandise}</p>
                  {doc.quantite && <p><strong>Quantité :</strong> {doc.quantite} {doc.unite_quantite || ''}</p>}
                  {doc.date_livraison && <p><strong>Date de livraison :</strong> {doc.date_livraison}</p>}
                  {(doc.lieu_depart || doc.lieu_arrivee) && (
                    <p><strong>Trajet :</strong> {doc.lieu_depart || '?'} → {doc.lieu_arrivee || '?'}</p>
                  )}
                </div>
              )}
              {doc.montant_ht && (
                <p className="text-lg font-semibold text-emerald-900 mt-3">
                  💰 Montant : {doc.montant_ht.toFixed(2)} € HT
                </p>
              )}
            </div>
          </div>

          {signed ? (
            <div className="text-center py-12" data-testid="sign-success">
              <CheckCircle className="h-24 w-24 text-emerald-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-emerald-800 mb-2">Signé !</h2>
              <p className="text-slate-600 mb-6">
                Merci. Le document a été archivé automatiquement dans Google Drive.
                Une copie a été envoyée par email.
              </p>
              {driveLink && (
                <a href={driveLink} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 bg-emerald-700 text-white px-6 py-3 rounded-lg hover:bg-emerald-800">
                  <ExternalLink className="w-4 h-4" /> Ouvrir dans Drive
                </a>
              )}
              <div className="mt-4">
                <a href={`${API}/api/signatures/public/sign/${token}/pdf`}
                   target="_blank" rel="noopener noreferrer"
                   className="text-emerald-700 hover:underline text-sm">
                  Télécharger le PDF signé
                </a>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {doc.message_personnalise && (
                <div className="bg-amber-50 p-3 rounded text-sm italic border-l-4 border-amber-400">
                  « {doc.message_personnalise} »
                </div>
              )}

              {/* Visualisation PDF */}
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <a href={`${API}/api/signatures/public/sign/${token}/pdf`}
                   target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 text-emerald-700 hover:underline font-semibold"
                   data-testid="view-pdf-btn">
                  <Eye className="w-5 h-5" /> Consulter le document intégral avant de signer
                </a>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Nom complet *</label>
                  <Input value={nomSignataire} onChange={e => setNomSignataire(e.target.value)}
                         placeholder="Prénom Nom" data-testid="nom-signataire" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Qualité / Fonction</label>
                  <Input value={qualite} onChange={e => setQualite(e.target.value)}
                         placeholder="Gérant, Chauffeur, Client..." />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 mb-2 flex items-center justify-between">
                  <span>Signature manuscrite *</span>
                  <button type="button" onClick={clearSign}
                          className="text-xs text-red-600 hover:underline flex items-center gap-1">
                    <Eraser className="w-3 h-3" /> Effacer
                  </button>
                </label>
                <canvas
                  ref={canvasRef}
                  width={900}
                  height={220}
                  style={{ touchAction: 'none', WebkitTapHighlightColor: 'transparent', WebkitUserSelect: 'none', userSelect: 'none' }}
                  className="border-2 border-dashed border-emerald-400 rounded-lg w-full h-[160px] sm:h-[200px] bg-white cursor-crosshair touch-none select-none"
                  data-testid="signature-pad"
                />
              </div>

              <label className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
                <Checkbox checked={accepte} onCheckedChange={setAccepte} />
                <span className="text-sm">
                  Je reconnais avoir pris connaissance du document et j'accepte son contenu.
                  Ma signature électronique a valeur juridique (eIDAS).
                </span>
              </label>

              <Button
                onClick={handleSign}
                disabled={signing || !nomSignataire || !accepte || !hasSignature}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white text-base py-6"
                data-testid="signer-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                {signing ? 'Signature en cours...' : `✍️ Signer ${typeLabel.toLowerCase()}`}
              </Button>

              <p className="text-xs text-slate-400 text-center">
                Mode : {doc.mode_signature === 'face_a_face' ? 'Face à face (terrain)' : 'Signature à distance'}
                · Conforme eIDAS · IP, horodatage et User Agent tracés
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
